/**
 * Words put on top of the edit: a title, a label, a call to action. The text
 * block is laid out at a fixed reference size and scaled to the width its
 * layer is given, so it keeps its line breaks and shape at any size, the way
 * a picture keeps its proportions. Layout is pure (the measuring is handed
 * in), so the preview, the export and the tests all place it alike.
 */

/** How the words are set off from the picture behind them. */
export type TextLook = 'plain' | 'outline' | 'box' | 'tag';
export const TEXT_LOOKS: readonly TextLook[] = ['plain', 'outline', 'box', 'tag'];

export type TextFont = 'sans' | 'serif' | 'mono';
export const TEXT_FONTS: readonly TextFont[] = ['sans', 'serif', 'mono'];

export type TextAlign = 'left' | 'center' | 'right';
export const TEXT_ALIGNS: readonly TextAlign[] = ['left', 'center', 'right'];

/** Text colours on offer: content colours (like caption styles), not interface chrome. */
export const TEXT_COLORS = ['#ffffff', '#0b0b0c', '#c6f25e', '#ffe14d', '#ff6b5e', '#6cb8ff'] as const;
export type TextColor = (typeof TEXT_COLORS)[number];
/** A colour's name for translations: its hex digits. */
export const colorKey = (color: TextColor) => color.slice(1) as TextColor extends `#${infer Digits}` ? Digits : never;

export interface TextStyle {
  readonly look: TextLook;
  readonly font: TextFont;
  /** `#rrggbb`: the letters, or the tag behind them. */
  readonly color: string;
  readonly align: TextAlign;
  readonly bold: boolean;
}

export const DEFAULT_TEXT_STYLE: TextStyle = { look: 'plain', font: 'sans', color: '#ffffff', align: 'center', bold: true };

export const MAX_TEXT_LENGTH = 280;
export const MAX_TEXT_LINES = 8;

/**
 * System fonts only: workers can't see the page's web fonts, and the preview
 * must look exactly like the export.
 */
export const TEXT_FONT_FAMILIES: Readonly<Record<TextFont, string>> = {
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: 'ui-serif, "New York", Georgia, Cambria, "Times New Roman", serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
};

/** Font size the block is laid out at; drawing scales it to the layer's width. */
export const TEXT_REFERENCE_PX = 100;
const LINE_HEIGHT = 1.18;

/** Room around the letters, in ems: a tag or box needs a margin, an outline or shadow a little bleed. */
const PADDING: Readonly<Record<TextLook, { readonly x: number; readonly y: number }>> = {
  plain: { x: 0.12, y: 0.08 },
  outline: { x: 0.14, y: 0.1 },
  box: { x: 0.42, y: 0.2 },
  tag: { x: 0.5, y: 0.22 },
};

/** A CSS font shorthand for the style at `px` pixels. */
export const textFont = (style: Pick<TextStyle, 'bold' | 'font'>, px: number): string => `${style.bold ? 800 : 500} ${px}px ${TEXT_FONT_FAMILIES[style.font]}`;

/** The lines the text is set in (hard line breaks only; trailing spaces don't count). */
export const textLines = (text: string): string[] =>
  text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .slice(0, MAX_TEXT_LINES)
    .map((line) => line.trimEnd());

export const isBlankText = (text: string): boolean => text.trim().length === 0;

/** Measures one line set in `font` (a canvas's `measureText`, or an estimate where there is none). */
export type MeasureText = (line: string, font: string) => number;

export interface TextBlock {
  readonly lines: readonly { readonly text: string; readonly width: number }[];
  readonly fontSize: number;
  readonly lineHeight: number;
  readonly padX: number;
  readonly padY: number;
  /** Whole block, padding included, at the reference size. */
  readonly width: number;
  readonly height: number;
}

export function layoutText(text: string, style: TextStyle, measure: MeasureText): TextBlock {
  const fontSize = TEXT_REFERENCE_PX;
  const font = textFont(style, fontSize);
  const lines = textLines(text).map((line) => ({ text: line, width: line ? Math.max(0, measure(line, font)) : 0 }));
  const padX = PADDING[style.look].x * fontSize;
  const padY = PADDING[style.look].y * fontSize;
  const lineHeight = fontSize * LINE_HEIGHT;
  // An empty block keeps a small, grabbable shape.
  const textWidth = Math.max(fontSize * 0.6, ...lines.map((line) => line.width));
  return { lines, fontSize, lineHeight, padX, padY, width: textWidth + padX * 2, height: lines.length * lineHeight + padY * 2 };
}

/** Width / height of the block: what sizes the layer on the frame. */
export const textAspect = (block: Pick<TextBlock, 'width' | 'height'>): number => (block.height > 0 ? block.width / block.height : 1);

/** Where a line starts inside a block `width` wide (reference pixels). */
export function lineStart(block: TextBlock, lineWidth: number, align: TextAlign): number {
  const inner = block.width - block.padX * 2;
  if (align === 'left') return block.padX;
  if (align === 'right') return block.padX + inner - lineWidth;
  return block.padX + (inner - lineWidth) / 2;
}

/** An estimate where nothing can measure (tests, a browser without canvas): average glyph widths per font. */
export const estimateTextWidth: MeasureText = (line, font) => {
  const px = Number(/(\d+(?:\.\d+)?)px/.exec(font)?.[1] ?? TEXT_REFERENCE_PX);
  const perChar = font.includes('monospace') ? 0.6 : font.startsWith('800') ? 0.58 : 0.52;
  return [...line].length * px * perChar;
};

const HEX = /^#[0-9a-f]{6}$/i;

/** Relative luminance of `#rrggbb` (WCAG). */
function luminance(hex: string): number {
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** Black or white, whichever reads better on `hex` (the higher WCAG contrast). */
export function inkOn(hex: string): string {
  if (!HEX.test(hex)) return '#ffffff';
  const l = luminance(hex);
  return (l + 0.05) / 0.05 >= 1.05 / (l + 0.05) ? '#0b0b0c' : '#ffffff';
}

/** True for dark colours, which get a light outline instead of a black one. */
export const isDarkColor = (hex: string): boolean => HEX.test(hex) && luminance(hex) < 0.18;

const pick = <T extends string>(value: unknown, options: readonly T[], fallback: T): T => (options.includes(value as T) ? (value as T) : fallback);

/** A text style read back from storage, with anything unknown replaced by the default. */
export function parseTextStyle(value: unknown): TextStyle {
  const raw = (value && typeof value === 'object' ? value : {}) as Partial<Record<keyof TextStyle, unknown>>;
  return {
    look: pick(raw.look, TEXT_LOOKS, DEFAULT_TEXT_STYLE.look),
    font: pick(raw.font, TEXT_FONTS, DEFAULT_TEXT_STYLE.font),
    color: typeof raw.color === 'string' && HEX.test(raw.color) ? raw.color.toLowerCase() : DEFAULT_TEXT_STYLE.color,
    align: pick(raw.align, TEXT_ALIGNS, DEFAULT_TEXT_STYLE.align),
    bold: typeof raw.bold === 'boolean' ? raw.bold : DEFAULT_TEXT_STYLE.bold,
  };
}

/**
 * Text as it may be kept: no longer than the limit, no more lines than fit.
 * Spaces are left alone (this runs on every keystroke; a word is still being typed).
 */
export const cleanText = (text: string): string => text.replace(/\r\n?/g, '\n').slice(0, MAX_TEXT_LENGTH).split('\n').slice(0, MAX_TEXT_LINES).join('\n');
